import type { HelpContent } from '../types'

/** Help panel + first-run walkthrough copy for Campaigns. Em-dash free. */
export const CAMPAIGNS_HELP: HelpContent = {
  key: 'campaigns',
  title: 'Campaigns',
  intro:
    'A campaign is a saved outreach sequence plus the leads it contacts. You build the message steps once, add leads, and turn it on. The system then sends everything for you, paced safely and inside your business hours.',
  panel: [
    {
      heading: 'The statuses',
      items: [
        { term: 'Draft', text: 'Built but not started. Activate it when you are ready.' },
        { term: 'Active', text: 'Live and sending, inside your daily limit and business hours.' },
        { term: 'Paused', text: 'Stopped. Leads and progress are kept; activate again anytime.' },
        { term: 'Completed', text: 'Finished its sequence for every lead.' },
        { term: 'Deleted', text: 'Archived, not erased. Its leads are unassigned, not deleted.' },
      ],
    },
    {
      heading: 'Creating one (the wizard)',
      items: [
        { term: 'Details', text: 'Name the campaign, choose the call to action, and paste the link the AI should send. You can also add up to 5 extra links or attachments (links to hosted files, like a deck or case study) and tell the AI when to send each one.' },
        { term: 'Leads', text: 'Pick who to contact, up to your plan per-campaign cap. No leads yet? Use Find new leads or Import by URL right inside this step. Your setup is saved while you add them, and a banner brings you straight back.' },
        { term: 'Sequence', text: 'Set the message steps and paste a few of your real messages so the AI writes in your voice.' },
        { term: 'Preview', text: 'See sample drafts for a placeholder prospect. This is free and sends nothing.' },
        { term: 'Review', text: 'Save as a draft, or activate now. Nothing is saved until you click Create.' },
      ],
    },
    {
      heading: 'The sequence',
      items: [
        {
          text: 'Every campaign starts with a connection request, then a first message after it is accepted. Those two steps are fixed because LinkedIn requires that order.',
        },
        {
          text: 'After that you add follow-ups. Each step has a delay measured in days.',
        },
        {
          text: 'You never have to pick a message strategy. The AI chooses and varies the angle for each lead automatically. If you want to force one for a step, open Advanced: message angle on that step.',
        },
      ],
    },
    {
      heading: 'Reading the numbers',
      items: [
        {
          text: 'Each card shows leads, connections sent, leads messaged, replies, and meetings. Reply rate is replies divided by messages, and it stays at zero until messages send.',
        },
      ],
    },
    {
      heading: 'Good to know',
      items: [
        { text: 'Activating needs a connected LinkedIn account and at least one lead.' },
        { text: 'A lower daily limit is safer for your account; a higher one reaches leads faster.' },
        { text: 'A lead can be in only one campaign at a time.' },
        { text: 'An active campaign only sends during your business hours, so it can look idle at night.' },
        { term: 'Who writes the messages', text: 'DealCloser AI writes by default. Pick Your Claude on a campaign to let your own connected Claude write its messages instead (set it up in Settings, Claude).' },
      ],
    },
  ],
  tour: [
    {
      id: 'welcome',
      title: 'Your outreach campaigns',
      body: 'A campaign is a message sequence plus the leads it contacts. Build it once, add leads, and turn it on.',
    },
    {
      id: 'new',
      target: 'campaigns-new',
      title: 'Start a campaign',
      body: 'New Campaign opens a short wizard: name it, pick leads, build the message steps, preview drafts, then save or activate.',
    },
    {
      id: 'stats',
      target: 'campaigns-stats',
      title: 'Your campaigns at a glance',
      body: 'How many are active, the total leads across them, and your average reply rate.',
    },
    {
      id: 'filters',
      target: 'campaigns-filters',
      title: 'Filter by status',
      body: 'Draft is not started, Active is sending, Paused is stopped. Deleted campaigns are archived, not erased.',
    },
    {
      id: 'card',
      target: 'campaigns-grid',
      title: 'Each campaign',
      body: 'A card shows its status, sequence length, daily limit, and results. Activate or pause it here, or open it for full detail.',
    },
    {
      id: 'done',
      title: 'One thing to remember',
      body: 'A campaign only sends once it is Active, has a connected LinkedIn account, and at least one lead. Reopen this from Help anytime.',
    },
  ],
}
